
//
//  components/ui/FooterOfficial/FooterOfficial.tsx
//  TownLIT
//
//  Created by Hossein Sakkaki on 2026-08-19.
//  Last Update by Hossein Sakkaki on 2026-10-19.
//

"use client";

import Link from "next/link";

import { useAppSelector } from "../../../redux/hooks";

import {
  selectTermsAndPolicies,
  selectMainLoading,
  selectMainError,
} from "../../../redux/selectors/main/mainSelectors";

import { TOWNLIT_ROUTES } from "../../../config/townlitDistribution";

import "./FooterOfficial.css";

const useInviteCode =
  String(
    process.env.NEXT_PUBLIC_USE_INVITE_CODE ?? "",
  )
    .trim()
    .toLowerCase() === "true";

const FooterOfficial = () => {
  const termsAndPolicies =
    useAppSelector(selectTermsAndPolicies);

  const isLoading =
    useAppSelector(
      selectMainLoading,
    ).fetchTerms;

  const error =
    useAppSelector(selectMainError);

  const officialPolicies =
    termsAndPolicies.filter(
      (term) =>
        term.is_active &&
        term.show_in_footer === true &&
        (
          term.display_location === "footer" ||
          term.display_location === "both" ||
          term.display_location === "official"
        ),
    );

  const leftColumnPolicies =
    officialPolicies.filter(
      (term) =>
        term.footer_column === "left",
    );

  const rightColumnPolicies =
    officialPolicies.filter(
      (term) =>
        term.footer_column !== "left",
    );

  return (
    <footer className="footer-container">
      <div className="footer-top">
        <div className="footer-support">
          <Link
            href="/"
            className="footer-support-link"
            title="Go to TownLIT home"
          >
            Home
          </Link>

          <Link
            href={TOWNLIT_ROUTES.app}
            className="footer-support-link"
            title="Get TownLIT on your device"
          >
            Get TownLIT
          </Link>

          <Link
            href="/wayhood"
            className="footer-support-link"
            title="Discover Wayhood, the digital category introduced by TownLIT"
          >
            What Is a Wayhood?
          </Link>

          <Link
            href="/townlit-official/support"
            className="footer-support-link"
            title="See how you can support TownLIT"
          >
            Ways to Support
          </Link>

          <Link
            href="/townlit-official/help-support"
            className="footer-support-link"
            title="Get help or contact TownLIT Support"
          >
            Help &amp; Support
          </Link>

          <Link
            href="/townlit-official/company"
            className="footer-support-link"
            title="Learn about LITCore Software Inc. and TownLIT"
          >
            Company
          </Link>

          {useInviteCode && (
            <Link
              href="/townlit-official/request-access"
              className="footer-support-link"
            >
              Be Invited
            </Link>
          )}

          {leftColumnPolicies.map(
            (term) => (
              <Link
                key={term.slug}
                href={`/townlit-official/foundation/${term.slug}`}
                className="footer-support-link"
              >
                {term.title}
              </Link>
            ),
          )}
        </div>

        <div className="footer-terms">
          {isLoading && (
            <p className="footer-message">
              Loading official documents...
            </p>
          )}

          {error && (
            <p className="footer-message">
              Failed to load official policies.
            </p>
          )}

          {!isLoading &&
            !error &&
            rightColumnPolicies.map(
              (term) => (
                <Link
                  key={term.slug}
                  href={`/townlit-official/foundation/${term.slug}`}
                  className="footer-link"
                >
                  {term.title}
                </Link>
              ),
            )}
        </div>
      </div>

      <div className="footer-copy">
        <p>
          &copy; 2022–
          {new Date().getFullYear()} LITCore
          Software Inc. All rights reserved.
        </p>

        <p>
          TownLIT<sup>™</sup> — established in
          2022 and operated by LITCore Software
          Inc.
        </p>
      </div>
    </footer>
  );
};

export default FooterOfficial;
